/**
 * Every screen the application has, in one place.
 *
 * A view is declared here and referenced by key everywhere else — the router,
 * the sidebar, the mobile tab bar and `ProtectedRoute` all read this table
 * rather than repeating a path string. The point is that a screen's path and
 * the conditions for reaching it are stated once.
 *
 * `permission` (a codename, or several meaning "any of them") and `anyRoles`
 * gate a view against `/auth/me/`: `canSeeView` consults them and every
 * existing view keeps working because not having them means not gating. The
 * backend remains the authority — hiding a screen here is a courtesy, and the
 * API still answers 403 to anything a role lacks.
 */
import type { CurrentUser } from '@/api/types';

/**
 * The codenames the frontend cares about, mirroring `seed_roles` in
 * kdoshsop-api. A new codename ships twice: registered for seeding back-end
 * side, declared here for the UI.
 */
export const PERMISSIONS = {
  productObservationsView: 'product_observations.view_productobservation',
  productObservationsViewAll: 'product_observations.view_all_productobservation',
  productObservationsAdd: 'product_observations.add_productobservation',
} as const;

export interface ViewDef {
  path: string;
  /** i18n key. A view without one is reachable but never appears in navigation. */
  labelKey?: string;
  /** Requires `is_staff`. Today that means "can also reach the Django admin". */
  staffOnly?: boolean;
  /**
   * Required permission(s). An array means "any of these" — the shared
   * warehouse bandeja is reachable with `view_productobservation` (own site)
   * or `view_all_productobservation` (whole history).
   */
  permission?: string | readonly string[];
  /** Required role codes. An array means "any of these". */
  anyRoles?: readonly string[];
}

export const VIEWS = {
  home: { path: '/', labelKey: 'nav.home' },
  productObservations: {
    path: '/productos-observados',
    labelKey: 'nav.productObservations',
    permission: PERMISSIONS.productObservationsAdd,
  },
  warehouseProductObservations: {
    path: '/productos-observados/almacen',
    labelKey: 'nav.warehouseObservations',
    permission: [PERMISSIONS.productObservationsViewAll, PERMISSIONS.productObservationsView],
  },
  productObservationDetail: {
    path: '/productos-observados/:id',
    permission: [PERMISSIONS.productObservationsViewAll, PERMISSIONS.productObservationsView],
  },
  sunatCommunicationBatches: {
    path: '/comunicaciones-sunat',
    labelKey: 'nav.sunatCommunicationBatches',
  },
  sunatCommunicationBatchDetail: { path: '/comunicaciones-sunat/:id' },
  changePassword: { path: '/cambiar-contrasena' },
} satisfies Record<string, ViewDef>;

export type ViewKey = keyof typeof VIEWS;

/** Views that can appear in navigation, i.e. the ones with a label. */
export type NavigableViewKey = {
  [K in ViewKey]: (typeof VIEWS)[K] extends { labelKey: string } ? K : never;
}[ViewKey];

function permissionNames(permission: string | readonly string[]): readonly string[] {
  return typeof permission === 'string' ? [permission] : permission;
}

/**
 * UI convenience only. The API remains the authority for every operation —
 * hiding a screen is a courtesy, not a control.
 */
export function canSeeView(user: CurrentUser | null, view: ViewDef): boolean {
  if (!user) return false;
  if (user.is_superuser) return true;
  if (view.staffOnly && !user.is_staff) return false;
  if (view.permission) {
    const needed = permissionNames(view.permission);
    if (!needed.some((codename) => user.permissions.includes(codename))) return false;
  }
  if (view.anyRoles && !view.anyRoles.some((code) => user.role_codes.includes(code))) return false;
  return true;
}
